/** Licensed Unsplash photography used only on non-product application surfaces. */
export type BgImage = { id: string; credit: string; focal: string }

export type SceneId =
  | 'welcome' | 'auth' | 'buyer' | 'seller' | 'warehouse'
  | 'courier' | 'assessor' | 'admin' | 'analytics' | 'info'

export type TextureId =
  | 'wheel' | 'air-filter' | 'engine-part' | 'vehicle-part'
  | 'suspension' | 'catalytic' | 'engines' | 'carburetor'

export const BG_SCENES: Record<SceneId, { light: BgImage; dark: BgImage }> = {
  welcome: {
    light: { id: '1606577924006-27d39b132ae2', credit: 'C Joyful', focal: '60% 42%' },
    dark: { id: '1596986952526-3be237187071', credit: 'Laurel & Michael Evans', focal: '60% 45%' },
  },
  auth: {
    light: { id: '1615906655593-ad0386982a0f', credit: 'Sten Rademaker', focal: '62% 45%' },
    dark: { id: '1556448851-9359658faa54', credit: 'Richard Biros', focal: '67% 45%' },
  },
  buyer: {
    light: { id: '1767341372150-b9521fec39a3', credit: 'Zoshua Colah', focal: '60% 44%' },
    dark: { id: '1777213003360-0419fd2fbfdf', credit: 'Diego Romeo', focal: '62% 40%' },
  },
  seller: {
    light: { id: '1553413077-190dd305871c', credit: 'Ruchindra Gunasekara', focal: '52% 48%' },
    dark: { id: '1627309366653-2dedc084cdf1', credit: 'Jacques Dillies', focal: '57% 42%' },
  },
  warehouse: {
    light: { id: '1586528116022-aeda1613c63d', credit: 'Adrian Sulyok', focal: '52% 46%' },
    dark: { id: '1644079446600-219068676743', credit: 'Lance Chang', focal: '55% 48%' },
  },
  courier: {
    light: { id: '1565891741441-64926e441838', credit: 'Marcin Jozwiak', focal: '50% 49%' },
    dark: { id: '1578991132108-16c5296b63dc', credit: 'Julian Hochgesang', focal: '52% 50%' },
  },
  assessor: {
    light: { id: '1487754180451-c456f719a1fc', credit: 'Tim Mossholder', focal: '60% 44%' },
    dark: { id: '1527383418406-f85a3b146499', credit: 'Tim Mossholder', focal: '58% 42%' },
  },
  admin: {
    light: { id: '1429772011165-0c2e054367b8', credit: 'Garett Mizunaka', focal: '55% 44%' },
    dark: { id: '1663642775693-6628f65358be', credit: 'Manuel E Sankitts', focal: '55% 42%' },
  },
  analytics: {
    light: { id: '1517026575980-3e1e2dedeab4', credit: 'Chris Liverani', focal: '58% 47%' },
    dark: { id: '1498887960847-2a5e46312788', credit: 'CHUTTERSNAP', focal: '60% 45%' },
  },
  info: {
    light: { id: '1772057593749-4038650963d4', credit: 'Vlad Ionita', focal: '53% 47%' },
    dark: { id: '1765446004204-aa52f4c11167', credit: 'Zoshua Colah', focal: '53% 47%' },
  },
}

export const CARD_TEXTURES: Record<TextureId, BgImage> = {
  wheel: { id: '1611633235555-45e252fe48c8', credit: 'José Pinto', focal: '70% 50%' },
  'air-filter': { id: '1522598140461-ec9911e01c53', credit: 'Rahul Bhogal', focal: '62% 50%' },
  'engine-part': { id: '1595787142842-7404bc60470d', credit: 'Julian Hochgesang', focal: '58% 50%' },
  'vehicle-part': { id: '1537462715879-360eeb61a0ad', credit: 'Sam Loyd', focal: '62% 50%' },
  suspension: { id: '1769218401807-5495675a2eb1', credit: 'Zoshua Colah', focal: '65% 50%' },
  catalytic: { id: '1759419281480-bacc913c9606', credit: 'engin akyurt', focal: '62% 50%' },
  engines: { id: '1763836393379-68f9721966ee', credit: 'Bernd Dittrich', focal: '60% 50%' },
  carburetor: { id: '1767340078306-89630b12d81c', credit: 'Zoshua Colah', focal: '62% 50%' },
}

export const AUTH_ASIDE_IMAGE: BgImage = {
  id: '1556448851-9359658faa54', credit: 'Richard Biros', focal: '65% 50%',
}
export const WELCOME_CTA_IMAGE: BgImage = {
  id: '1519752594763-2633d8d4ea29', credit: 'lee attwood', focal: '50% 50%',
}

export const TEXTURE_IDS = Object.keys(CARD_TEXTURES) as TextureId[]

export function unsplashUrl(id: string, { w, q }: { w: number; q: number }): string {
  return `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=${q}`
}

export function sceneForPath(pathname: string): SceneId {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/'
  if (path === '/') return 'welcome'
  if (path === '/auth' || path.startsWith('/auth/')) return 'auth'
  if (path === '/seller/warehouse' || path.startsWith('/seller/warehouse/') ||
      path === '/admin/warehouse' || path.startsWith('/admin/warehouse/') ||
      path === '/seller/inventory' || path.startsWith('/seller/inventory/')) return 'warehouse'
  if (path === '/admin/dashboard' || path === '/seller/dashboard' ||
      path === '/buyer/wallet' ||
      /\/(reports|profits|analytics)(\/|$)/.test(path)) return 'analytics'
  if (path === '/buyer' || path.startsWith('/buyer/')) return 'buyer'
  if (path === '/seller' || path.startsWith('/seller/')) return 'seller'
  if (path === '/courier' || path.startsWith('/courier/')) return 'courier'
  if (path === '/quality-assessor' || path.startsWith('/quality-assessor/')) return 'assessor'
  if (path === '/admin' || path.startsWith('/admin/')) return 'admin'
  return 'info'
}

export function shouldLoadPhotos(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  const network = navigator as Navigator & { connection?: { saveData?: boolean } }
  return !network.connection?.saveData &&
    !window.matchMedia('(prefers-reduced-data: reduce)').matches &&
    !window.matchMedia('(prefers-contrast: more)').matches &&
    !window.matchMedia('(forced-colors: active)').matches
}
